import React, { Component, MouseEvent } from 'react';
import FormicaInput from '../../custom/FormicaInput';
import { AnimatedButton } from '@trlogic/ui-pack';
import { MultiSelectChangeParams } from 'primereact/multiselect';
import CustomMultiSelect from '../../custom/CustomMultiSelect';
import { WatchlistConfiguration, WatchlistListsModel } from '../../../domain/watchlist/WatchlistDomains';
import WatchlistService from '../../../service/WatchlistService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomTable from '../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import MessageUtil from '../../../util/MessageUtil';
import Icon from '../../../icons/Icon';
import LanguageUtil from '../../../util/LanguageUtil';
import { User } from 'domain/rbac/RbacDomains';
import { RootState } from 'redux/store';
import { ConnectedProps, connect } from 'react-redux';

class WatchlistConfigurationComponent extends Component<Props, State> {
  public state = new State();
  private watchlistService: WatchlistService = ServiceContainer.getInstance().get(ServiceNames.WATCHLIST);

  render() {
    const { name, selectedLists } = this.state;
    const { watchlistConfiguration, user } = this.props;
    const isAdmin = user.role == 'Admin' || user.role == 'Root' || user.roles.includes('Admin');

    return (
      <div className={'w-full p-5'}>
        <div className={'w-full flex flex-col lg:flex-row justify-around items-center gap-5'}>
          <div className={'w-full flex'}>
            <FormicaInput
              onChange={(e: React.FormEvent<HTMLInputElement>) => this.onChangeName(e)}
              value={name}
              name={'Group Name'}
              label={LanguageUtil.get({ id: 'common.label.groupName', defaultMessage: 'Group Name' })}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.groupName', defaultMessage: 'Group Name' })}
              type={'text'}
              disabled={!isAdmin}
            />
          </div>
          <div className={'w-full min-w-0 group flex gap-x-5 gap-y-5'}>
            <CustomMultiSelect
              items={this.getListSelectOptions()}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.lists', defaultMessage: 'Lists' })}
              selectedItems={selectedLists}
              label={LanguageUtil.get({ id: 'common.label.lists', defaultMessage: 'Lists' })}
              onChange={(e: MultiSelectChangeParams) => this.onSelectListsField(e)}
              disabled={!isAdmin}
            />
          </div>
          <div className={'h-full flex justify-center items-center'}>
            <AnimatedButton
              text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
              icon={'add'}
              customSize={'sm'}
              customType={'primary'}
              disabled={!isAdmin}
              onClick={this.addConfiguration}
            />
          </div>
        </div>
        <div className={'py-4'}>
          <CustomTable value={watchlistConfiguration} columns={this.getColumns(isAdmin)} />
        </div>
      </div>
    );
  }

  private onChangeName = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({ name: (e.target as HTMLInputElement).value });
  };

  private onSelectListsField = (e: MultiSelectChangeParams) => {
    if (e.value) {
      this.setState({
        selectedLists: e.value,
      });
    }
  };

  private onSelectUpdateListsField = (e: MultiSelectChangeParams, record: WatchlistConfiguration) => {
    const updatedRecord = { ...record, watchlists: e.value };
    this.saveConfiguration(updatedRecord);
  };

  private addConfiguration = async () => {
    const { name, selectedLists } = this.state;
    const { watchlistConfiguration } = this.props;
    if (name != undefined && name.trim().length != 0 && selectedLists != undefined && selectedLists.length != 0) {
      const existingGroup = watchlistConfiguration.find((group) => group.name === name);
      if (!existingGroup) {
        const data: WatchlistConfiguration = { id: `group_${name}`, name: name, watchlists: selectedLists };
        await this.saveConfiguration(data);
        this.setState({
          selectedLists: [],
          name: '',
        });
      } else {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
          detail: LanguageUtil.get({ id: 'pages.watchlist.configuration.addError', defaultMessage: 'You cannot create groups with same name' }),
        });
      }
    }
  };

  private deleteConfiguration = async (record: WatchlistConfiguration) => {
    try {
      await this.watchlistService.deleteConfiguration(record.id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
        detail: LanguageUtil.get({ id: 'pages.watchlist.configuration.deleteSuccess', defaultMessage: `Group deleted` }),
      });
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
        detail: LanguageUtil.get({ id: 'pages.watchlist.configuration.error', defaultMessage: `Server Error` }),
      });
    }
  };

  private saveConfiguration = async (data: WatchlistConfiguration) => {
    try {
      await this.watchlistService.saveConfiguration(data);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
        detail: LanguageUtil.get({ id: 'pages.watchlist.configuration.saveSuccess', defaultMessage: `Group saved` }),
      });
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'pages.watchlist.title', defaultMessage: 'Watchlist' }),
        detail: LanguageUtil.get({ id: 'pages.watchlist.configuration.error', defaultMessage: `Server Error` }),
      });
    }
  };

  private getListSelectOptions = () => {
    const { lists } = this.props;
    if (lists == undefined || lists.length == 0) return [];
    return lists.map((value: WatchlistListsModel) => ({
      label: value.name,
      value: value.id,
    }));
  };

  private getColumns = (isAdmin: boolean = false): ColumnProps[] => {
    return [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.watchlist.configuration.table.name', defaultMessage: 'Name' }),
        className: 'w-2/6',
        headerClassName: 'h-[44px] 2xl:h-[52px]',
        body: (record: WatchlistConfiguration) => {
          return <span className={'pl-4'}>{record.name}</span>;
        },
      },
      {
        field: 'lists',
        header: LanguageUtil.get({ id: 'pages.watchlist.configuration.table.lists', defaultMessage: 'Lists' }),
        className: 'w-3/6',
        body: (record: WatchlistConfiguration) => {
          return (
            <div className={'!max-w-[600px] min-w-0 group flex gap-x-5 gap-y-5 overflow-hidden'}>
              <CustomMultiSelect
                items={this.getListSelectOptions()}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.lists', defaultMessage: 'Lists' })}
                selectedItems={record.watchlists}
                label={LanguageUtil.get({ id: 'common.label.lists', defaultMessage: 'Lists' })}
                onChange={(e: MultiSelectChangeParams) => this.onSelectUpdateListsField(e, record)}
                disabled={!isAdmin}
              />
            </div>
          );
        },
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.watchlist.configuration.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        alignHeader: 'center',
        sortable: false,
        body: (record: WatchlistConfiguration) => {
          return (
            <button
              className={`flex w-full justify-start items-center text-xs 2xl:text-md ml-2 ${isAdmin ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-30'}`}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                if (isAdmin) {
                  e.stopPropagation();
                  e.preventDefault();
                  this.deleteConfiguration(record);
                }
              }}
            >
              <Icon icon={'delete'} size={20} className='text-f-black2' />
            </button>
          );
        },
      },
    ];
  };
}

interface Props extends ReduxProps {
  watchlistConfiguration: WatchlistConfiguration[];
  lists: WatchlistListsModel[];
}

class State {
  name: string | undefined;
  selectedLists: string[] = [];
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user as User,
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(WatchlistConfigurationComponent);
